<template>
  <div class="boards-list">
    <div class="boards-header">
      <h1>Kanban Boards</h1>
      <p class="subtitle">Manage your workflow with custom Kanban boards</p>
    </div>

    <div class="boards-grid">
      <div v-for="board in boards" :key="board.id" class="board-card" @click="navigateToBoard(board)">
        <div class="board-card-header">
          <h3>{{ board.title }}</h3>
          <span class="board-bucket-count">{{ board.buckets.length }} buckets</span>
        </div>

        <div class="board-card-content">
          <div class="board-buckets">
            <div v-for="bucket in board.buckets.slice(0, 3)" :key="bucket.id" class="bucket-preview"
              :style="{ backgroundColor: bucket.color }">
              {{ bucket.name }}
            </div>
            <div v-if="board.buckets.length > 3" class="bucket-preview more">
              +{{ board.buckets.length - 3 }} more
            </div>
          </div>
        </div>

        <div class="board-card-footer">
          <span class="board-description">{{ getBoardDescription(board) }}</span>
        </div>
      </div>
    </div>

    <div v-if="boards.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="material-icons">dashboard</i>
      </div>
      <h3>No Boards Available</h3>
      <p>Boards will appear here once they are configured.</p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'BoardsList',
  computed: {
    ...mapState({
      boards: state => state.kanban.boards
    })
  },
  methods: {
    navigateToBoard(board) {
      this.$router.push({
        name: 'KanbanBoard',
        params: {
          boardId: board.id
        }
      })
    },
    getBoardDescription(board) {
      const bucketNames = board.buckets.map(b => b.name).join(', ')
      return `Contains ${board.buckets.length} buckets: ${bucketNames}`
    }
  }
}
</script>

<style lang="scss" scoped>
.boards-list {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.boards-header {
  text-align: center;
  margin-bottom: 40px;

  h1 {
    color: #333;
    margin-bottom: 8px;
    font-size: 32px;
    font-weight: 600;
  }

  .subtitle {
    color: #666;
    font-size: 16px;
    margin: 0;
  }
}

.boards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.board-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: #1976d2;
  }
}

.board-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    color: #333;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
  }

  .board-bucket-count {
    background: #f5f5f5;
    color: #666;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }
}

.board-card-content {
  margin-bottom: 16px;
}

.board-buckets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bucket-preview {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

  &.more {
    background: #e0e0e0;
    color: #666;
    text-shadow: none;
  }
}

.board-card-footer {
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;

  .board-description {
    color: #666;
    font-size: 14px;
    line-height: 1.4;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;

  .empty-icon {
    margin-bottom: 24px;

    .material-icons {
      font-size: 64px;
      color: #ccc;
    }
  }

  h3 {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 24px;
    font-weight: 500;
  }

  p {
    margin: 0;
    font-size: 16px;
  }
}

// Responsive design
@media (max-width: 768px) {
  .boards-list {
    padding: 16px;
  }

  .boards-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .board-card {
    padding: 20px;
  }

  .boards-header h1 {
    font-size: 28px;
  }
}
</style>
